import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { AssessmentIcon, CalendarIcon, InPersonFilled } from '@components/icons'
import { useQueryParams } from '@hooks'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import {
  useClinicianSchedulingControllerGetNextAppointmentDate,
  useSessionControllerGetDemoSuggestedContent,
  useSessionControllerGetSuggestedContent
} from '~/clinician-api'
import { TreatmentPlan } from '../../components'
import { SuggestionLoader } from '../assist-panel'
import {
  AssessmentSuggestionModule,
  InterventionSuggestionModule,
  WorksheetSuggestionModule
} from '../assist-panel/suggestion-modules'
import SubtleLoading from '../shared/assist/svgs/subtle-loader'
import { DisclaimerBanner } from '../shared/disclaimer-banner'
import { ConsiderationLink } from './ConsiderationLink'

interface Client {
  id: string
  first_name: string
  is_demo: boolean
  isAssistSetupComplete: boolean
}

interface ConsiderationsPanelProps {
  client: Client
  setIsTxPlanModalOpen: (isOpen: boolean) => void
  isTxPlanModalOpen: boolean
  sessionId: string
  toggleAssistOverlay: () => void
}

// Reusable component for assist setup sections
const AssistSetupSection = ({
  icon: Icon,
  title,
  description,
  isSetupComplete,
  onExplore,
  onSetup,
  isExpanded,
  children,
  clientName
}: {
  icon: React.ComponentType<{ width?: number; height?: number; fill?: string }>
  title: string
  description: string
  isSetupComplete: boolean
  onExplore: () => void
  onSetup: () => void
  isExpanded?: boolean
  children?: React.ReactNode
  clientName: string
}) => {
  if (isExpanded) {
    return <Box mt="24px">{children}</Box>
  }

  return (
    <Box display="flex" flexDirection="column" pl="16px" mt="24px">
      <Flex align="center" gap="12px">
        <Icon width={20} height={20} fill="black" />
        <Text fontWeight="bold" fontSize="sm">
          {title}
        </Text>
      </Flex>
      <Text fontSize="xsm" color="dark_gray" mt="8px">
        {isSetupComplete
          ? description
          : `Want treatment ideas for ${clientName}? Set a few preferences to unlock personalized suggestions.`}
      </Text>
      <ConsiderationLink
        colorScheme="primary"
        w="fit-content"
        mt="12px"
        onClick={isSetupComplete ? onExplore : onSetup}
      >
        {isSetupComplete ? 'Explore' : 'Set up'}
      </ConsiderationLink>
    </Box>
  )
}

export const ConsiderationsPanel = ({
  client,
  setIsTxPlanModalOpen,
  isTxPlanModalOpen,
  sessionId,
  toggleAssistOverlay
}: ConsiderationsPanelProps) => {
  const [exploreInterventionsClicked, setExploreInterventionsClicked] =
    useState(false)
  const [exploreAssessmentsClicked, setExploreAssessmentsClicked] =
    useState(false)
  const [exploreWorksheetsClicked, setExploreWorksheetsClicked] =
    useState(false)
  const [showDemoLoading, setShowDemoLoading] = React.useState<boolean>(true)
  const queryParams = useQueryParams()
  const demoContext = queryParams.get('context')
  const isDemoQueryParam = queryParams.get('isDemo') === 'true'

  const { data: suggestions, isLoading } =
    useSessionControllerGetSuggestedContent(sessionId, {
      query: {
        refetchInterval: data => {
          if (data?.isGenerationComplete === false) {
            return 5000
          }

          return false
        }
      }
    })

  const showDemoExperience = client.is_demo

  const { data: demoData } = useSessionControllerGetDemoSuggestedContent(
    sessionId,
    { demoContext: demoContext || 'post-session' },
    {
      query: {
        enabled: showDemoExperience
      }
    }
  )

  useEffect(() => {
    if (isDemoQueryParam) {
      setShowDemoLoading(true)
    }
  }, [isDemoQueryParam])

  useEffect(() => {
    if (showDemoExperience && !isDemoQueryParam) {
      setTimeout(() => {
        setShowDemoLoading(false)
      }, 2000)
    } else if (!showDemoExperience && !isDemoQueryParam) {
      setShowDemoLoading(false)
    }
  }, [showDemoExperience, isDemoQueryParam])

  const assessmentSuggestion = showDemoExperience
    ? demoData?.suggestedAssessments[0]
    : suggestions?.suggestedAssessments[0]
  const worksheetSuggestion = showDemoExperience
    ? demoData?.suggestedWorksheets[0]
    : suggestions?.suggestedWorksheets[0]
  const interventionSuggestion = showDemoExperience
    ? demoData?.suggestedInterventions[0]
    : suggestions?.suggestedInterventions[0]

  const showDisclaimer =
    assessmentSuggestion || worksheetSuggestion || interventionSuggestion

  const handleSetupClick = () => {
    toggleAssistOverlay()
  }

  const { data: nextAppointmentData } =
    useClinicianSchedulingControllerGetNextAppointmentDate(client.id)
  const nextAppointmentDateFormatted = nextAppointmentData?.date
    ? {
        date: moment(nextAppointmentData.date).format('MMM D, YYYY'),
        time: moment(nextAppointmentData.date).format('ha'),
        isoDate: moment(nextAppointmentData.date).format('YYYY-MM-DD')
      }
    : null

  if (!client) {
    return null
  }

  if (showDemoLoading) {
    return (
      <Box p="32px">
        <SubtleLoading />
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box p="32px">
        <SuggestionLoader />
      </Box>
    )
  }

  return (
    <VStack gap="medium" p="medium" align="stretch" h="100%">
      {showDisclaimer && <DisclaimerBanner />}

      <Box display="flex" flexDirection="column">
        <Text fontSize="md" fontWeight="bold" color="primary">
          Plan Ahead
        </Text>

        {/* Update Treatment Plan */}
        <Box display="flex" flexDirection="column" pl="16px" mt="24px">
          <TreatmentPlan
            client={client}
            isModalOpenProp={isTxPlanModalOpen}
            setIsModalOpen={setIsTxPlanModalOpen}
            isConsiderationsPanel
          />
        </Box>

        {/* Explore interventions */}
        <AssistSetupSection
          icon={InPersonFilled}
          title="Explore interventions"
          description={`We've found a few activities to consider running in your next session with ${client?.first_name}`}
          isSetupComplete={client.isAssistSetupComplete}
          onExplore={() => setExploreInterventionsClicked(true)}
          onSetup={handleSetupClick}
          isExpanded={exploreInterventionsClicked}
          clientName={client?.first_name}
        >
          <InterventionSuggestionModule
            sessionId={sessionId}
            client={client}
            suggestions={suggestions}
            showDemoExperience={showDemoExperience}
            demoData={demoData}
          />
        </AssistSetupSection>

        {/* Confirm next appointment */}
        <Box display="flex" flexDirection="column" pl="16px" mt="24px">
          <Flex align="center" gap="12px">
            <CalendarIcon width={20} height={20} fill="black" />
            <Text fontWeight="bold" fontSize="sm">
              Confirm next appointment
            </Text>
          </Flex>
          <Text color="dark_gray" mt="8px">
            {nextAppointmentDateFormatted ? (
              <Box>
                <Text fontSize="xsm">
                  {client?.first_name}'s next appointment is scheduled for
                </Text>
                <Text fontSize="xsm" as="span" fontWeight="bold">
                  {nextAppointmentDateFormatted.date}
                </Text>
                <Text fontSize="xsm" as="span">
                  {' at '}
                </Text>
                <Text fontSize="xsm" as="span">
                  {nextAppointmentDateFormatted.time}
                </Text>
              </Box>
            ) : (
              <Text fontSize="xsm">
                There's no appointment on the schedule for {client?.first_name}
              </Text>
            )}
          </Text>
          <ConsiderationLink
            colorScheme="primary"
            w="fit-content"
            mt="12px"
            target="_blank"
            rightIcon={
              <OpenInNewIcon style={{ width: '16px', height: '16px' }} />
            }
            href={`/calendar?date=${encodeURIComponent(
              nextAppointmentDateFormatted?.isoDate || ''
            )}`}
          >
            {nextAppointmentDateFormatted ? 'View' : 'Schedule'}
          </ConsiderationLink>
        </Box>
      </Box>

      {/* Stay Connected Section */}
      <Box display="flex" flexDirection="column">
        <Text fontSize="md" fontWeight="bold" color="primary">
          Stay Connected
        </Text>

        {/* View worksheets */}
        <AssistSetupSection
          icon={AssessmentIcon}
          title="View worksheets"
          description={`Explore these personalized worksheet recommendations for ${client?.first_name}`}
          isSetupComplete={client.isAssistSetupComplete}
          onExplore={() => setExploreWorksheetsClicked(true)}
          onSetup={handleSetupClick}
          isExpanded={exploreWorksheetsClicked}
          clientName={client?.first_name}
        >
          <WorksheetSuggestionModule
            sessionId={sessionId}
            client={client}
            suggestions={suggestions}
            showDemoExperience={showDemoExperience}
            demoData={demoData}
          />
        </AssistSetupSection>

        {/* View assessments */}
        <AssistSetupSection
          icon={AssessmentIcon}
          title="View assessments"
          description={`Explore these personalized assessment recommendations for ${client?.first_name}`}
          isSetupComplete={client.isAssistSetupComplete}
          onExplore={() => setExploreAssessmentsClicked(true)}
          onSetup={handleSetupClick}
          isExpanded={exploreAssessmentsClicked}
          clientName={client?.first_name}
        >
          <AssessmentSuggestionModule
            sessionId={sessionId}
            client={client}
            suggestions={suggestions}
            showDemoExperience={showDemoExperience}
            demoData={demoData}
          />
        </AssistSetupSection>
      </Box>
    </VStack>
  )
}

export default ConsiderationsPanel
